import { useState, type FormEvent } from "react";
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Group,
  Modal,
  MultiSelect,
  NumberInput,
  PasswordInput,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import {
  IconAdjustments,
  IconKey,
  IconPencil,
  IconTrash,
} from "@tabler/icons-react";
import {
  apiDelete,
  apiFetch,
  apiPatch,
  apiPost,
  apiPut,
  friendlyError,
  type ApiError,
} from "../../api/client.js";
import { useAsync } from "../../lib/useAsync.js";
import type { AdminAppHost } from "./adminAppUtils.js";

interface Quota {
  cpu_cores: number;
  ram_mb: number;
  disk_gb: number;
}
interface Customer {
  id: number;
  username: string;
  display_name: string;
  email: string | null;
  enabled: boolean;
  created_at: string;
  quota: Quota | null;
  vm_count: number;
  vmid_prefix: string | null;
  pve_pool: string | null;
  backup_storage: string | null;
  storage_pools: string[] | null;
  vlan_tag: number | null;
  net_mtu: number | null;
  app_host_id: number | null;
  app_host_name: string | null;
}

// Cluster PBS datastores from GET /api/admin/storages (no node param —
// tri-mode route). Populates the per-customer backup storage picker.
interface BackupStorage {
  id: string;
  type: string;
  content: string;
  enabled: boolean;
}

// Cluster storages that can hold VM disks AND container rootfs, from
// GET /api/admin/storages?kind=disk (Phase 25). Populates the per-customer
// storage pools picker.
interface DiskStorage {
  id: string;
  type: string;
  content: string;
  enabled: boolean;
}

const USERNAME_RE = /^[a-zA-Z0-9_-]{3,32}$/;

interface CreateForm {
  username: string;
  password: string;
  display_name: string;
  email: string;
  cpu_cores: number | string;
  ram_mb: number | string;
  disk_gb: number | string;
  vmid_prefix: string;
  pve_pool: string;
  backup_storage: string;
  storage_pools: string[];
  vlan_tag: string;
  net_mtu: string;
}
const EMPTY_CREATE: CreateForm = {
  username: "",
  password: "",
  display_name: "",
  email: "",
  cpu_cores: 2,
  ram_mb: 2048,
  disk_gb: 40,
  vmid_prefix: "",
  pve_pool: "",
  backup_storage: "",
  storage_pools: [],
  vlan_tag: "",
  net_mtu: "",
};

// Editable fields on an existing customer. Username is intentionally NOT
// here — it's the customer's identity and never changes; password has its
// own reset flow; quota has its own endpoint. vmid_prefix + pve_pool ARE
// editable here so operators can opt existing customers into Phase 10
// (prefix/pool) without recreating them.
interface EditForm {
  display_name: string;
  email: string;
  enabled: boolean;
  vmid_prefix: string;
  pve_pool: string;
  backup_storage: string;
  storage_pools: string[];
  vlan_tag: string;
  net_mtu: string;
  app_host_id: string;
}

// Quota is edited through its own endpoint (PUT /customers/:id/quota) and
// gets its own action + modal, separate from the profile Edit modal. The
// backend upserts the quota row; old vs new is captured in the audit log.
interface QuotaForm {
  cpu_cores: number | string;
  ram_mb: number | string;
  disk_gb: number | string;
}

function fmtRam(mb: number): string {
  if (mb >= 1024 && mb % 1024 === 0) return `${mb / 1024} GB`;
  return `${mb} MB`;
}

export function Customers() {
  const { data, loading, error, refetch } = useAsync<Customer[]>(
    () => apiFetch<Customer[]>("/api/admin/customers"),
    []
  );
  const { data: backupStorages } = useAsync<BackupStorage[]>(
    () => apiFetch<BackupStorage[]>("/api/admin/storages"),
    []
  );
  const backupStorageOptions = (backupStorages ?? []).map((s) => ({
    value: s.id,
    label: `${s.id} (PBS)`,
  }));
  const { data: diskStorages } = useAsync<DiskStorage[]>(
    () => apiFetch<DiskStorage[]>("/api/admin/storages?kind=disk"),
    []
  );
  const diskStorageOptions = (diskStorages ?? []).map((s) => ({
    value: s.id,
    label: s.id,
  }));
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<CreateForm>(EMPTY_CREATE);
  const [createErrors, setCreateErrors] = useState<Record<string, string>>({});
  const [creating, setCreating] = useState(false);

  const [resetTarget, setResetTarget] = useState<Customer | null>(null);
  const [resetPw, setResetPw] = useState("");
  const [resetError, setResetError] = useState<string | null>(null);
  const [resetting, setResetting] = useState(false);

  const [editTarget, setEditTarget] = useState<Customer | null>(null);
  const [editForm, setEditForm] = useState<EditForm | null>(null);
  const [editErrors, setEditErrors] = useState<Record<string, string>>({});
  const [editing, setEditing] = useState(false);

  // App-host pin options — fetched lazily the first time the Edit modal
  // opens (empty selection = unpinned → default host wins).
  const [appHosts, setAppHosts] = useState<AdminAppHost[] | null>(null);
  const [appHostsLoading, setAppHostsLoading] = useState(false);

  async function loadAppHosts() {
    setAppHostsLoading(true);
    try {
      const hosts = await apiFetch<AdminAppHost[]>("/api/admin/app-hosts");
      setAppHosts(hosts);
    } catch {
      setAppHosts(null);
    } finally {
      setAppHostsLoading(false);
    }
  }

  const [quotaTarget, setQuotaTarget] = useState<Customer | null>(null);
  const [quotaForm, setQuotaForm] = useState<QuotaForm | null>(null);
  const [quotaErrors, setQuotaErrors] = useState<Record<string, string>>({});
  const [savingQuota, setSavingQuota] = useState(false);

  function openCreate() {
    setCreateForm(EMPTY_CREATE);
    setCreateErrors({});
    setCreateOpen(true);
  }

  function validateCreate(f: CreateForm): Record<string, string> {
    const e: Record<string, string> = {};
    if (!USERNAME_RE.test(f.username)) {
      e.username = "3-32 chars: letters, numbers, _ or -";
    }
    if (f.password.length < 10) e.password = "At least 10 characters";
    if (!f.display_name.trim()) e.display_name = "Required";
    if (f.email && !/^[^\s@]+@[^\s@]+$/.test(f.email)) e.email = "Invalid email";
    const cpu = Number(f.cpu_cores);
    if (!Number.isInteger(cpu) || cpu < 1 || cpu > 64) e.cpu_cores = "1-64";
    const ram = Number(f.ram_mb);
    if (!Number.isInteger(ram) || ram < 128 || ram > 262144) e.ram_mb = "128-262144";
    const disk = Number(f.disk_gb);
    if (!Number.isInteger(disk) || disk < 1 || disk > 10000) e.disk_gb = "1-10000";
    if (!/^[1-9][0-9]$/.test(f.vmid_prefix)) e.vmid_prefix = "2 digits, first 1-9 (e.g. 64)";
    if (f.pve_pool && !/^[A-Za-z0-9._-]{1,64}$/.test(f.pve_pool)) e.pve_pool = "Letters, numbers, ., _ or - (max 64)";
    const vlanTag = Number(f.vlan_tag);
    if (f.vlan_tag !== "" && (!Number.isInteger(vlanTag) || vlanTag < 0 || vlanTag > 4094)) {
      e.vlan_tag = "Integer 0-4094 (0 = untagged)";
    }
    const netMtu = Number(f.net_mtu);
    if (f.net_mtu !== "" && (!Number.isInteger(netMtu) || netMtu < 576 || netMtu > 65520)) {
      e.net_mtu = "Integer 576-65520";
    }
    return e;
  }

  async function submitCreate(e: FormEvent) {
    e.preventDefault();
    const errs = validateCreate(createForm);
    setCreateErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setCreating(true);
    try {
      await apiPost("/api/admin/customers", {
        username: createForm.username.trim(),
        password: createForm.password,
        display_name: createForm.display_name.trim(),
        ...(createForm.email ? { email: createForm.email.trim() } : {}),
        cpu_cores: Number(createForm.cpu_cores),
        ram_mb: Number(createForm.ram_mb),
        disk_gb: Number(createForm.disk_gb),
        vmid_prefix: createForm.vmid_prefix,
        ...(createForm.pve_pool.trim() ? { pve_pool: createForm.pve_pool.trim() } : {}),
        ...(createForm.backup_storage ? { backup_storage: createForm.backup_storage } : {}),
        ...(createForm.storage_pools.length ? { storage_pools: createForm.storage_pools } : {}),
        ...(createForm.vlan_tag !== "" ? { vlan_tag: Number(createForm.vlan_tag) } : {}),
        ...(createForm.net_mtu !== "" ? { net_mtu: Number(createForm.net_mtu) } : {}),
      });
      notifications.show({ color: "green", message: "Customer created" });
      setCreateOpen(false);
      refetch();
    } catch (err) {
      const apiErr = err as ApiError;
      if (apiErr.status === 409 && apiErr.error === "username_taken") {
        setCreateErrors({ username: "Username already taken" });
      } else if (apiErr.status === 409 && apiErr.error === "vmid_prefix_taken") {
        setCreateErrors({ vmid_prefix: "Already assigned to another customer" });
      } else if (apiErr.status === 409 && apiErr.error === "backup_storage_unknown") {
        setCreateErrors({ backup_storage: "Unknown storage — refresh and pick a valid PBS datastore" });
      } else if (apiErr.status === 409 && apiErr.error === "storage_pool_unknown") {
        setCreateErrors({ storage_pools: "Unknown or non-disk storage — refresh and pick again" });
      } else {
        notifications.show({ color: "red", message: friendlyError(apiErr) });
      }
    } finally {
      setCreating(false);
    }
  }

  function openReset(c: Customer) {
    setResetTarget(c);
    setResetPw("");
    setResetError(null);
  }

  function openEdit(c: Customer) {
    setEditTarget(c);
    setEditForm({
      display_name: c.display_name,
      email: c.email ?? "",
      enabled: c.enabled,
      vmid_prefix: c.vmid_prefix ?? "",
      pve_pool: c.pve_pool ?? "",
      backup_storage: c.backup_storage ?? "",
      storage_pools: c.storage_pools ?? [],
      vlan_tag: c.vlan_tag === null ? "" : String(c.vlan_tag),
      net_mtu: c.net_mtu === null ? "" : String(c.net_mtu),
      app_host_id: c.app_host_id === null ? "" : String(c.app_host_id),
    });
    setEditErrors({});
    if (appHosts === null) void loadAppHosts();
  }

  function validateEdit(f: EditForm): Record<string, string> {
    const e: Record<string, string> = {};
    if (!f.display_name.trim()) e.display_name = "Required";
    if (f.email && !/^[^\s@]+@[^\s@]+$/.test(f.email)) e.email = "Invalid email";
    if (!/^[1-9][0-9]$/.test(f.vmid_prefix)) e.vmid_prefix = "2 digits, first 1-9 (e.g. 64)";
    if (f.pve_pool && !/^[A-Za-z0-9._-]{1,64}$/.test(f.pve_pool)) {
      e.pve_pool = "Letters, numbers, ., _ or - (max 64)";
    }
    const vlanTag = Number(f.vlan_tag);
    if (f.vlan_tag !== "" && (!Number.isInteger(vlanTag) || vlanTag < 0 || vlanTag > 4094)) {
      e.vlan_tag = "Integer 0-4094 (0 = untagged)";
    }
    const netMtu = Number(f.net_mtu);
    if (f.net_mtu !== "" && (!Number.isInteger(netMtu) || netMtu < 576 || netMtu > 65520)) {
      e.net_mtu = "Integer 576-65520";
    }
    return e;
  }

  async function submitEdit(e: FormEvent) {
    e.preventDefault();
    if (!editTarget || !editForm) return;
    const errs = validateEdit(editForm);
    setEditErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setEditing(true);
    try {
      await apiPatch(`/api/admin/customers/${editTarget.id}`, {
        display_name: editForm.display_name.trim(),
        email: editForm.email.trim() || null,
        enabled: editForm.enabled,
        vmid_prefix: editForm.vmid_prefix,
        pve_pool: editForm.pve_pool.trim() || null,
        backup_storage: editForm.backup_storage || null,
        storage_pools: editForm.storage_pools.length ? editForm.storage_pools : null,
        vlan_tag: editForm.vlan_tag === "" ? null : Number(editForm.vlan_tag),
        net_mtu: editForm.net_mtu === "" ? null : Number(editForm.net_mtu),
        app_host_id: editForm.app_host_id === "" ? null : Number(editForm.app_host_id),
      });
      notifications.show({ color: "green", message: "Customer updated" });
      setEditTarget(null);
      setEditForm(null);
      refetch();
    } catch (err) {
      const apiErr = err as ApiError;
      if (apiErr.status === 409 && apiErr.error === "vmid_prefix_taken") {
        setEditErrors({ vmid_prefix: "Already assigned to another customer" });
      } else if (apiErr.status === 409 && apiErr.error === "backup_storage_unknown") {
        setEditErrors({ backup_storage: "Unknown storage — refresh and pick a valid PBS datastore" });
      } else if (apiErr.status === 409 && apiErr.error === "storage_pool_unknown") {
        setEditErrors({ storage_pools: "Unknown or non-disk storage — refresh and pick again" });
      } else if (apiErr.status === 409 && apiErr.error === "unknown_app_host") {
        setEditErrors({ app_host_id: "App host no longer exists — refresh and pick again" });
      } else {
        notifications.show({ color: "red", message: friendlyError(apiErr) });
      }
    } finally {
      setEditing(false);
    }
  }

  async function submitReset(e: FormEvent) {
    e.preventDefault();
    if (!resetTarget) return;
    if (resetPw.length < 10) {
      setResetError("At least 10 characters");
      return;
    }
    setResetting(true);
    try {
      await apiPost(`/api/admin/customers/${resetTarget.id}/reset-password`, {
        password: resetPw,
      });
      notifications.show({ color: "green", message: "Password reset" });
      setResetTarget(null);
    } catch (err) {
      setResetError(friendlyError(err));
    } finally {
      setResetting(false);
    }
  }

  function openQuota(c: Customer) {
    setQuotaTarget(c);
    // Seed the form from the customer's existing quota (if any). Defaults
    // mirror EMPTY_CREATE so a customer with no quota gets sane starting
    // values rather than empty inputs.
    setQuotaForm({
      cpu_cores: c.quota?.cpu_cores ?? 2,
      ram_mb: c.quota?.ram_mb ?? 2048,
      disk_gb: c.quota?.disk_gb ?? 40,
    });
    setQuotaErrors({});
  }

  function validateQuota(f: QuotaForm): Record<string, string> {
    const e: Record<string, string> = {};
    const cpu = Number(f.cpu_cores);
    if (!Number.isInteger(cpu) || cpu < 1 || cpu > 64) e.cpu_cores = "1-64";
    const ram = Number(f.ram_mb);
    if (!Number.isInteger(ram) || ram < 128 || ram > 262144) e.ram_mb = "128-262144";
    const disk = Number(f.disk_gb);
    if (!Number.isInteger(disk) || disk < 1 || disk > 10000) e.disk_gb = "1-10000";
    return e;
  }

  async function submitQuota(e: FormEvent) {
    e.preventDefault();
    if (!quotaTarget || !quotaForm) return;
    const errs = validateQuota(quotaForm);
    setQuotaErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setSavingQuota(true);
    try {
      await apiPut(`/api/admin/customers/${quotaTarget.id}/quota`, {
        cpu_cores: Number(quotaForm.cpu_cores),
        ram_mb: Number(quotaForm.ram_mb),
        disk_gb: Number(quotaForm.disk_gb),
      });
      notifications.show({ color: "green", message: "Quota updated" });
      setQuotaTarget(null);
      setQuotaForm(null);
      refetch();
    } catch (err) {
      notifications.show({ color: "red", message: friendlyError(err) });
    } finally {
      setSavingQuota(false);
    }
  }

  function confirmDelete(c: Customer) {
    modals.openConfirmModal({
      title: "Delete customer",
      centered: true,
      children: (
        <Text size="sm">
          Delete <b>{c.username}</b>? This cannot be undone.
        </Text>
      ),
      labels: { confirm: "Delete", cancel: "Cancel" },
      confirmProps: { color: "red" },
      onConfirm: async () => {
        try {
          await apiDelete(`/api/admin/customers/${c.id}`);
          notifications.show({ color: "green", message: "Customer deleted" });
          refetch();
        } catch (err) {
          const apiErr = err as ApiError;
          if (apiErr.status === 409 && apiErr.error === "has_vms") {
            notifications.show({
              color: "red",
              message: "Cannot delete customer with VMs assigned.",
            });
          } else {
            notifications.show({ color: "red", message: friendlyError(apiErr) });
          }
        }
      },
    });
  }

  return (
    <Box>
      <Group justify="space-between" mb="md">
        <Title order={2}>Customers</Title>
        <Button onClick={openCreate}>Create customer</Button>
      </Group>

      {error && (
        <Text c="red" mb="sm">
          {error}
        </Text>
      )}
      <Table striped highlightOnHover withTableBorder>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>ID</Table.Th>
            <Table.Th>Username</Table.Th>
            <Table.Th>Display name</Table.Th>
            <Table.Th>Email</Table.Th>
            <Table.Th>Prefix</Table.Th>
            <Table.Th>Enabled</Table.Th>
            <Table.Th>Quota</Table.Th>
            <Table.Th>VMs</Table.Th>
            <Table.Th>Apps host</Table.Th>
            <Table.Th>Actions</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {loading && (
            <Table.Tr>
              <Table.Td colSpan={10}>
                <Text c="dimmed">Loading…</Text>
              </Table.Td>
            </Table.Tr>
          )}
          {!loading && data && data.length === 0 && (
            <Table.Tr>
              <Table.Td colSpan={10}>
                <Text c="dimmed">No customers yet.</Text>
              </Table.Td>
            </Table.Tr>
          )}
          {data?.map((c) => (
            <Table.Tr key={c.id}>
              <Table.Td>{c.id}</Table.Td>
              <Table.Td>{c.username}</Table.Td>
              <Table.Td>{c.display_name}</Table.Td>
              <Table.Td>{c.email ?? "—"}</Table.Td>
              <Table.Td>{c.vmid_prefix ?? "—"}</Table.Td>
              <Table.Td>
                <Badge color={c.enabled ? "green" : "gray"} variant="filled">
                  {c.enabled ? "enabled" : "disabled"}
                </Badge>
              </Table.Td>
              <Table.Td>
                {c.quota
                  ? `${c.quota.cpu_cores} CPU · ${fmtRam(c.quota.ram_mb)} · ${c.quota.disk_gb} GB`
                  : "—"}
              </Table.Td>
              <Table.Td>{c.vm_count}</Table.Td>
              <Table.Td>{c.app_host_name ?? "—"}</Table.Td>
              <Table.Td>
                <Group gap="xs">
                  <Tooltip label="Edit profile" position="bottom" withArrow>
                    <ActionIcon
                      variant="default"
                      size="lg"
                      onClick={() => openEdit(c)}
                      aria-label="Edit profile"
                    >
                      <IconPencil size={18} stroke={1.75} />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label="Edit quota" position="bottom" withArrow>
                    <ActionIcon
                      variant="default"
                      size="lg"
                      onClick={() => openQuota(c)}
                      aria-label="Edit quota"
                    >
                      <IconAdjustments size={18} stroke={1.75} />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label="Reset password" position="bottom" withArrow>
                    <ActionIcon
                      variant="default"
                      size="lg"
                      onClick={() => openReset(c)}
                      aria-label="Reset password"
                    >
                      <IconKey size={18} stroke={1.75} />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label="Delete customer" position="bottom" withArrow>
                    <ActionIcon
                      variant="default"
                      color="red"
                      size="lg"
                      onClick={() => confirmDelete(c)}
                      aria-label="Delete customer"
                    >
                      <IconTrash size={18} stroke={1.75} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      <Modal opened={createOpen} onClose={() => setCreateOpen(false)} title="Create customer" centered>
        <form onSubmit={submitCreate}>
          <Stack gap="sm">
            <TextInput
              label="Username"
              value={createForm.username}
              onChange={(e) => setCreateForm({ ...createForm, username: e.target.value })}
              error={createErrors.username}
            />
            <PasswordInput
              label="Password"
              value={createForm.password}
              onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
              error={createErrors.password}
            />
            <TextInput
              label="Display name"
              value={createForm.display_name}
              onChange={(e) => setCreateForm({ ...createForm, display_name: e.target.value })}
              error={createErrors.display_name}
            />
            <TextInput
              label="Email (optional)"
              value={createForm.email}
              onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
              error={createErrors.email}
            />
            <Group grow>
              <NumberInput
                label="Max CPU cores per VM/CT"
                description="Per-VM/CT cap. Aggregate RAM/Disk limits below still apply across all of the customer's VMs."
                value={createForm.cpu_cores}
                onChange={(v) => setCreateForm({ ...createForm, cpu_cores: (v as number) ?? "" })}
                min={1}
                max={64}
                error={createErrors.cpu_cores}
              />
              <NumberInput
                label="RAM (MB)"
                value={createForm.ram_mb}
                onChange={(v) => setCreateForm({ ...createForm, ram_mb: (v as number) ?? "" })}
                min={128}
                max={262144}
                error={createErrors.ram_mb}
              />
            </Group>
            <NumberInput
              label="Disk (GB)"
              value={createForm.disk_gb}
              onChange={(v) => setCreateForm({ ...createForm, disk_gb: (v as number) ?? "" })}
              min={1}
              max={10000}
              error={createErrors.disk_gb}
            />
            <Group grow>
              <TextInput
                label="VMID prefix"
                description="2 digits, first 1-9. Defines this customer's VM/CT ID range (e.g. 64 → 64001-64999)."
                placeholder="64"
                value={createForm.vmid_prefix}
                onChange={(e) => setCreateForm({ ...createForm, vmid_prefix: e.target.value })}
                error={createErrors.vmid_prefix}
              />
              <TextInput
                label="PVE pool (optional)"
                description="Resource pool name. If set, all customer VMs are added to this pool."
                placeholder="customer-pool-a"
                value={createForm.pve_pool}
                onChange={(e) => setCreateForm({ ...createForm, pve_pool: e.target.value })}
                error={createErrors.pve_pool}
              />
            </Group>
            <Group grow align="flex-start">
              <NumberInput
                label="VLAN tag"
                description="Empty = default (customer VMID prefix) · 0 = untagged"
                value={createForm.vlan_tag}
                onChange={(v) => setCreateForm({ ...createForm, vlan_tag: String(v ?? "") })}
                min={0}
                max={4094}
                allowDecimal={false}
                error={createErrors.vlan_tag}
              />
              <NumberInput
                label="Network MTU"
                description="Empty = 1400"
                value={createForm.net_mtu}
                onChange={(v) => setCreateForm({ ...createForm, net_mtu: String(v ?? "") })}
                min={576}
                max={65520}
                allowDecimal={false}
                error={createErrors.net_mtu}
              />
            </Group>
            <Select
              label="Backup storage"
              description="PBS datastore for this customer's nightly VM/CT backups. Empty = backups not offered."
              placeholder="No backup storage"
              data={backupStorageOptions}
              value={createForm.backup_storage || null}
              onChange={(v) => setCreateForm({ ...createForm, backup_storage: v ?? "" })}
              clearable
              error={createErrors.backup_storage}
            />
            <MultiSelect
              label="Storage pools"
              description="VM/CT disk storages the backend auto-selects from for this customer (customers don't pick storage). Empty = inherit the global set."
              placeholder="Inherit global"
              data={diskStorageOptions}
              value={createForm.storage_pools}
              onChange={(v) => setCreateForm({ ...createForm, storage_pools: v })}
              clearable
              searchable
              error={createErrors.storage_pools}
            />
            <Button type="submit" loading={creating} mt="xs">
              Create
            </Button>
          </Stack>
        </form>
      </Modal>

      <Modal
        opened={resetTarget !== null}
        onClose={() => setResetTarget(null)}
        title={`Reset password — ${resetTarget?.username ?? ""}`}
        centered
      >
        <form onSubmit={submitReset}>
          <Stack gap="sm">
            <PasswordInput
              label="New password"
              value={resetPw}
              onChange={(e) => setResetPw(e.target.value)}
              error={resetError}
              autoFocus
            />
            <Button type="submit" loading={resetting}>
              Reset password
            </Button>
          </Stack>
        </form>
      </Modal>

      <Modal
        opened={editTarget !== null}
        onClose={() => {
          setEditTarget(null);
          setEditForm(null);
          setEditErrors({});
        }}
        title={`Edit customer — ${editTarget?.username ?? ""}`}
        centered
        size="lg"
      >
        {editForm && (
          <form onSubmit={submitEdit}>
            <Stack gap="sm">
              <Text size="sm" c="dimmed">
                Username: <b>{editTarget?.username}</b> (not editable)
              </Text>
              <TextInput
                label="Display name"
                value={editForm.display_name}
                onChange={(e) => setEditForm({ ...editForm, display_name: e.target.value })}
                error={editErrors.display_name}
              />
              <TextInput
                label="Email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                placeholder="(none)"
                error={editErrors.email}
              />
              <Group grow align="flex-start">
                <TextInput
                  label="VMID prefix"
                  description="2 digits, first 1-9. Range: prefix×1000+1 to +999."
                  placeholder="64"
                  value={editForm.vmid_prefix}
                  onChange={(e) => setEditForm({ ...editForm, vmid_prefix: e.target.value })}
                  error={editErrors.vmid_prefix}
                />
                <TextInput
                  label="PVE pool"
                  description="Resource pool name. Empty = no pool."
                  placeholder="(none)"
                  value={editForm.pve_pool}
                  onChange={(e) => setEditForm({ ...editForm, pve_pool: e.target.value })}
                  error={editErrors.pve_pool}
                />
              </Group>
              <Group grow align="flex-start">
                <NumberInput
                  label="VLAN tag"
                  description="Empty = default (customer VMID prefix) · 0 = untagged"
                  value={editForm.vlan_tag}
                  onChange={(v) => setEditForm({ ...editForm, vlan_tag: String(v ?? "") })}
                  min={0}
                  max={4094}
                  allowDecimal={false}
                  error={editErrors.vlan_tag}
                />
                <NumberInput
                  label="Network MTU"
                  description="Empty = 1400"
                  value={editForm.net_mtu}
                  onChange={(v) => setEditForm({ ...editForm, net_mtu: String(v ?? "") })}
                  min={576}
                  max={65520}
                  allowDecimal={false}
                  error={editErrors.net_mtu}
                />
              </Group>
              <Select
                label="Backup storage"
                description="PBS datastore for this customer's nightly VM/CT backups. Empty = backups not offered."
                placeholder="No backup storage"
                data={backupStorageOptions}
                value={editForm.backup_storage || null}
                onChange={(v) => setEditForm({ ...editForm, backup_storage: v ?? "" })}
                clearable
                error={editErrors.backup_storage}
              />
              <MultiSelect
                label="Storage pools"
              description="VM/CT disk storages the backend auto-selects from for this customer (customers don't pick storage). Empty = inherit the global set."
                placeholder="Inherit global"
                data={diskStorageOptions}
                value={editForm.storage_pools}
                onChange={(v) => setEditForm({ ...editForm, storage_pools: v })}
                clearable
                searchable
                error={editErrors.storage_pools}
              />
              <Select
                label="App host"
                description="Docker app host this customer's apps deploy to. Empty = default host."
                placeholder={appHostsLoading ? "Loading…" : "Default host"}
                data={(appHosts ?? []).map((h) => ({
                  value: String(h.id),
                  label: h.name,
                  disabled: !h.enabled,
                }))}
                value={editForm.app_host_id || null}
                onChange={(v) => setEditForm({ ...editForm, app_host_id: v ?? "" })}
                clearable
                error={editErrors.app_host_id}
              />
              <Switch
                label="Enabled"
                description="Disabled customers cannot log in."
                checked={editForm.enabled}
                onChange={(e) => setEditForm({ ...editForm, enabled: e.currentTarget.checked })}
              />
              <Button type="submit" loading={editing} mt="xs">
                Save changes
              </Button>
            </Stack>
          </form>
        )}
      </Modal>

      <Modal
        opened={quotaTarget !== null}
        onClose={() => {
          setQuotaTarget(null);
          setQuotaForm(null);
          setQuotaErrors({});
        }}
        title={`Edit quota — ${quotaTarget?.username ?? ""}`}
        centered
      >
        {quotaForm && (
          <form onSubmit={submitQuota}>
            <Stack gap="sm">
              <Text size="sm" c="dimmed">
                Current:{" "}
                {quotaTarget?.quota
                  ? `${quotaTarget.quota.cpu_cores} CPU · ${fmtRam(quotaTarget.quota.ram_mb)} · ${quotaTarget.quota.disk_gb} GB`
                  : "—"}
              </Text>
              <NumberInput
                label="Max CPU cores per VM/CT"
                description="Per-VM/CT cap. Aggregate RAM/Disk limits below still apply across all of the customer's VMs."
                value={quotaForm.cpu_cores}
                onChange={(v) => setQuotaForm({ ...quotaForm, cpu_cores: (v as number) ?? "" })}
                min={1}
                max={64}
                error={quotaErrors.cpu_cores}
              />
              <NumberInput
                label="Total RAM (MB)"
                value={quotaForm.ram_mb}
                onChange={(v) => setQuotaForm({ ...quotaForm, ram_mb: (v as number) ?? "" })}
                min={128}
                max={262144}
                error={quotaErrors.ram_mb}
              />
              <NumberInput
                label="Total disk (GB)"
                value={quotaForm.disk_gb}
                onChange={(v) => setQuotaForm({ ...quotaForm, disk_gb: (v as number) ?? "" })}
                min={1}
                max={10000}
                error={quotaErrors.disk_gb}
              />
              <Button type="submit" loading={savingQuota} mt="xs">
                Save quota
              </Button>
            </Stack>
          </form>
        )}
      </Modal>
    </Box>
  );
}
